   import CheckIcon from "@mui/icons-material/Check";
   import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
   import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
   import StarIcon from "@mui/icons-material/Star";
   import StarBorderIcon from "@mui/icons-material/StarBorder";
   import {
     Alert,
     Avatar,
     Box,
     Chip,
     Divider,
     IconButton,
     MenuItem,
     Popover,
     TextField,
     Tooltip,
     Typography,
   } from "@mui/material";
   import { useAuth0 } from "@auth0/auth0-react";
   import React, { useEffect, useMemo, useState } from "react";
   import { useDispatch } from "react-redux";
   import { useLocation, useNavigate } from "react-router-dom";
   import { useApps, useAppId, useHomeApp } from "../../hook";
   import { useWorkspaceFavorites } from "../../hook/workspaceFavorites";
   import { useSetHomeApp, useSetSelectedApp } from "../../hook/settings";
   import { useWorkspaceSwitching } from "../../context/workspaceSwitching";
   import { ActionType, AppDispatch } from "../../reducer";
   import { orderWorkspaces } from "./homeWorkspace";

   // The Home Workspace chip, on the Home row only (query-omega-oql pages/oql/terms.html, Home Workspace).
   const HomeBadge: React.FC<{ testId: string }> = ({ testId }) => (
     <Chip
       data-testid={testId}
       label="Home"
       size="small"
       sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, flexShrink: 0 }}
     />
   );

   export const WorkspaceSwitcher: React.FC = () => {
     const dispatch = useDispatch<AppDispatch>();
     const navigate = useNavigate();
     const { pathname } = useLocation();
     const [apps] = useApps();
     const [appId] = useAppId();
     const [homeApp] = useHomeApp();
     const setSelectedApp = useSetSelectedApp();
     const setHomeApp = useSetHomeApp();
     const { user } = useAuth0();
     const { setIsSwitching } = useWorkspaceSwitching();
     const { favorites, isFavorite, toggleFavorite, isSaving, error, clearError } =
       useWorkspaceFavorites();
     const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
     const [searchString, setSearchString] = useState("");
     const [activeIndex, setActiveIndex] = useState(-1);
     const open = Boolean(anchorEl);

     // Home, then favorites alphabetically, then get-apps order; the local search filters that list.
     const orderedApps = useMemo(
       () => orderWorkspaces(apps, homeApp, favorites),
       [apps, homeApp, favorites]
     );
     const filteredApps = useMemo(() => {
       const query = searchString.trim().toLowerCase();
       if (!query) return orderedApps;
       return orderedApps.filter((app) =>
         app.name.toLowerCase().includes(query)
       );
     }, [orderedApps, searchString]);

     const activeApp =
       activeIndex >= 0 && activeIndex < filteredApps.length
         ? filteredApps[activeIndex]
         : undefined;

     // Keep the keyboard highlight inside the filtered list as the query or the favorites change.
     useEffect(() => {
       if (!open) return;
       setActiveIndex((current) => {
         if (filteredApps.length === 0) return -1;
         if (current < 0) return 0;
         return Math.min(current, filteredApps.length - 1);
       });
     }, [filteredApps, open]);

     const currentApp = apps.find((app) => app.id === appId);

     const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
       clearError();
       setSearchString("");
       const index = orderedApps.findIndex((app) => app.id === appId);
       setActiveIndex(index >= 0 ? index : orderedApps.length > 0 ? 0 : -1);
       setAnchorEl(event.currentTarget);
     };

     const handleClose = () => {
       setAnchorEl(null);
       setSearchString("");
       setActiveIndex(-1);
       clearError();
     };

     const handleSelect = (selectedAppId: string) => {
       if (selectedAppId === appId) {
         handleClose();
         return;
       }
       handleClose();
       setIsSwitching(true);
       setSelectedApp({ appId: selectedAppId, email: user?.email! })
         .then(() => {
           dispatch({ type: ActionType.RESET_NOTEBOOK_WORKSPACE });
           if (pathname !== "/settings") {
             navigate(`/app/${selectedAppId}/notebook`);
           }
         })
         .finally(() => setIsSwitching(false));
     };

     // "Set as home" changes only the Home Workspace (a write to system:#home): the picker stays open, the
     // selection and route are unchanged. stopPropagation keeps the row's select from running.
     const handleSetHome = (event: React.MouseEvent, homeAppId: string) => {
       event.stopPropagation();
       setHomeApp({ appId: homeAppId });
     };

     const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
       setSearchString(event.currentTarget.value);
       setActiveIndex(0);
     };

     // The search field owns the keyboard while the picker is open: arrows move a clamped highlight,
     // Enter selects it through the same path as a row click, Escape closes.
     const handleSearchKeyDown = (
       event: React.KeyboardEvent<HTMLInputElement>
     ) => {
       if (event.key === "ArrowDown") {
         event.preventDefault();
         if (filteredApps.length === 0) return;
         setActiveIndex((current) =>
           Math.min(current < 0 ? 0 : current + 1, filteredApps.length - 1)
         );
       } else if (event.key === "ArrowUp") {
         event.preventDefault();
         if (filteredApps.length === 0) return;
         setActiveIndex((current) => Math.max(current - 1, 0));
       } else if (event.key === "Enter") {
         event.preventDefault();
         if (activeApp) handleSelect(activeApp.id);
       } else if (event.key === "Escape") {
         event.preventDefault();
         handleClose();
       }
     };

     if (!currentApp) return null;

     const initial = currentApp.name.charAt(0).toUpperCase();

     return (
       <>
         <Box
           sx={{
             px: 1.5,
             pt: 0.5,
             pb: 1,
             borderBottom: "1px solid",
             borderColor: "divider",
           }}
         >
           <Typography
             sx={{
               display: "block",
               fontSize: "0.6rem",
               fontWeight: 700,
               letterSpacing: "0.1em",
               textTransform: "uppercase",
               color: "text.disabled",
               mb: 0.5,
               pl: 0.5,
             }}
           >
             Workspace
           </Typography>
           <Tooltip title="Switch workspace" placement="right" disableInteractive>
             <Box
               data-testid="workspace-switcher-current"
               onClick={handleOpen}
               role="button"
               aria-haspopup="listbox"
               sx={{
                 display: "flex",
                 alignItems: "center",
                 gap: 1,
                 px: 0.75,
                 py: 0.5,
                 borderRadius: 1,
                 cursor: "pointer",
                 transition: "background-color 0.15s",
                 "&:hover": { backgroundColor: "action.hover" },
               }}
             >
               <Avatar
                 sx={{
                   width: 24,
                   height: 24,
                   fontSize: "0.7rem",
                   fontWeight: 700,
                   bgcolor: "primary.main",
                   color: "#fff",
                   flexShrink: 0,
                 }}
               >
                 {initial}
               </Avatar>
               <Typography
                 noWrap
                 sx={{
                   fontSize: "0.825rem",
                   fontWeight: 600,
                   flex: 1,
                   color: "text.primary",
                 }}
               >
                 {currentApp.name}
               </Typography>
               <ExpandMoreIcon
                 sx={{
                   fontSize: "1rem",
                   color: "text.secondary",
                   flexShrink: 0,
                   transform: open ? "rotate(180deg)" : "rotate(0deg)",
                   transition: "transform 0.2s ease",
                 }}
               />
             </Box>
           </Tooltip>
         </Box>
         <Popover
           anchorEl={anchorEl}
           open={open}
           onClose={handleClose}
           anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
           transformOrigin={{ vertical: "top", horizontal: "left" }}
           PaperProps={{ elevation: 3, sx: { minWidth: 280, mt: 0.5 } }}
         >
           <Box sx={{ p: 1, width: "20rem" }}>
             <TextField
               fullWidth
               size="small"
               autoFocus
               value={searchString}
               onChange={handleSearchChange}
               placeholder="Search workspaces…"
               inputProps={{
                 "data-testid": "workspace-switcher-search",
                 "aria-label": "Search workspaces",
                 "aria-activedescendant": activeApp
                   ? `workspace-switcher-option-${activeApp.id}`
                   : undefined,
                 onKeyDown: handleSearchKeyDown,
               }}
             />
             {error && (
               <Alert
                 severity="error"
                 data-testid="workspace-switcher-favorites-error"
                 sx={{ mt: 1, fontSize: "0.75rem" }}
               >
                 {error}
               </Alert>
             )}
             <Divider sx={{ mt: 1 }} />
             <Box
               role="listbox"
               aria-label="Workspaces"
               sx={{ maxHeight: 320, overflowY: "auto", py: 0.5 }}
             >
               {filteredApps.map((app, index) => {
                 const isActive = app.id === appId;
                 const isHome = app.id === homeApp;
                 const starred = isFavorite(app.id);
                 const optionId = `workspace-switcher-option-${app.id}`;
                 return (
                   <MenuItem
                     key={app.id}
                     id={optionId}
                     data-testid={optionId}
                     data-active={index === activeIndex ? "true" : undefined}
                     role="option"
                     aria-selected={isActive}
                     onClick={() => handleSelect(app.id)}
                     dense
                     sx={{
                       py: 0.75,
                       gap: 1.5,
                       ...(index === activeIndex
                         ? { backgroundColor: "action.hover" }
                         : {}),
                     }}
                   >
                     <Avatar
                       sx={{
                         width: 22,
                         height: 22,
                         fontSize: "0.65rem",
                         fontWeight: 700,
                         bgcolor: isActive ? "primary.main" : "action.selected",
                         color: isActive ? "#fff" : "text.secondary",
                         flexShrink: 0,
                       }}
                     >
                       {app.name.charAt(0).toUpperCase()}
                     </Avatar>
                     <Typography sx={{ fontSize: "0.875rem", flex: 1 }}>
                       {app.name}
                     </Typography>
                     <Tooltip
                       title={
                         starred ? "Remove from favorites" : "Add to favorites"
                       }
                       placement="right"
                       disableInteractive
                     >
                       <IconButton
                         size="small"
                         data-testid={`workspace-switcher-star-${app.id}`}
                         data-favorite={starred ? "true" : "false"}
                         aria-label={
                           starred
                             ? `Remove ${app.name} from favorites`
                             : `Add ${app.name} to favorites`
                         }
                         disabled={isSaving}
                         onClick={(event) => {
                           event.stopPropagation();
                           toggleFavorite(app.id);
                         }}
                         sx={{
                           p: 0.25,
                           opacity: starred ? 1 : 0,
                           "li:hover &": { opacity: 1 },
                           "li:focus-within &": { opacity: 1 },
                         }}
                       >
                         {starred ? (
                           <StarIcon
                             sx={{ fontSize: "0.95rem", color: "warning.main" }}
                           />
                         ) : (
                           <StarBorderIcon sx={{ fontSize: "0.95rem" }} />
                         )}
                       </IconButton>
                     </Tooltip>
                     {isHome ? (
                       <HomeBadge
                         testId={`workspace-switcher-home-badge-${app.id}`}
                       />
                     ) : (
                       <Tooltip
                         title="Set as home"
                         placement="right"
                         disableInteractive
                       >
                         <IconButton
                           size="small"
                           aria-label="Set as home"
                           data-testid={`workspace-switcher-set-home-${app.id}`}
                           onClick={(event) => handleSetHome(event, app.id)}
                           sx={{ p: 0.25 }}
                         >
                           <HomeOutlinedIcon sx={{ fontSize: "0.95rem" }} />
                         </IconButton>
                       </Tooltip>
                     )}
                     {isActive && (
                       <CheckIcon
                         sx={{ fontSize: "0.875rem", color: "primary.main" }}
                       />
                     )}
                   </MenuItem>
                 );
               })}
               {filteredApps.length === 0 && (
                 <Box
                   data-testid="workspace-switcher-no-matches"
                   sx={{ px: 2, py: 1.5 }}
                 >
                   <Typography variant="body2" color="text.secondary">
                     No workspaces match
                   </Typography>
                 </Box>
               )}
             </Box>
           </Box>
         </Popover>
       </>
     );
   };
